"use client"

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { cn, buildRegionUrl } from '../lib/utils'
import { useRegion } from '../hooks/use-region'
import { useSession } from '@/features/auth/context/session.context'
import { updateSelectedRegion, getProfile } from '@/features/profile/api/profile.api'
import { useQueryClient } from '@tanstack/react-query'
import { UserRole } from '@/shared/constants/user-roles'
import { authFetchJson } from '@/shared/lib/auth-fetch'

/** Region slug for FL Harlem Heights — hidden from region switcher for non-Admin users. */
const HARLEM_HEIGHTS_SLUG = 'harlem-heights'

export interface LocationOption {
  id: string
  label: string
  slug: string
}

interface LocationSelectorProps {
  options?: LocationOption[]
  defaultLocation?: string
  onLocationChange?: (location: string) => void
  className?: string
}

function LocationSelector({ 
  options: providedOptions, 
  defaultLocation,
  onLocationChange,
  className 
}: LocationSelectorProps) {
  const { profile, setProfile, isLoading: sessionLoading, session } = useSession()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const searchParamsKey = searchParams.toString()
  const { regionSlug } = useRegion()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [fetchedOptions, setFetchedOptions] = useState<LocationOption[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const profileSelectedRegionId = profile?.selectedRegion
    ? profile.selectedRegion.toString()
    : null

  const options = useMemo(() => {
    if (sessionLoading) return fetchedOptions
    if (session?.role === UserRole.ADMIN) return fetchedOptions
    return fetchedOptions.filter((o) => o.slug !== HARLEM_HEIGHTS_SLUG)
  }, [fetchedOptions, session?.role, sessionLoading])

  // Fetch regions from API
  useEffect(() => {
    const fetchRegions = async () => {
      if (providedOptions) {
        setFetchedOptions(providedOptions)
        setLoading(false)
        return
      }

      try {
        const res = await authFetchJson<LocationOption[]>('/regions/options')
        const regions = Array.isArray(res) ? res : ((res as any)?.success ? (res as any).data : null)
        if (Array.isArray(regions)) {
          setFetchedOptions(regions)
        } else {
          console.error('Regions options API error:', res)
        }
      } catch (error) {
        console.error('Error fetching regions:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchRegions()
  }, [providedOptions])

  // Non-admin users cannot stay on Harlem Heights routes; send them to an allowed region
  useEffect(() => {
    if (sessionLoading || loading || options.length === 0) return
    if (session?.role === UserRole.ADMIN) return
    if (regionSlug !== HARLEM_HEIGHTS_SLUG) return

    const fromProfile = profileSelectedRegionId
      ? options.find((o) => o.id === profileSelectedRegionId)
      : undefined
    const target = fromProfile ?? options[0]
    if (!target?.slug) return

    const pathAfterRegion = pathname.replace(`/${regionSlug}`, '') || '/'
    const queryString = searchParamsKey
    const newPath = `/${target.slug}${pathAfterRegion}${queryString ? `?${queryString}` : ''}`

    void (async () => {
      try {
        const result = await updateSelectedRegion(target.id)
        if (result.success && result.data) {
          setProfile(result.data)
        }
      } catch {
        /* still navigate */
      }
      router.replace(newPath)
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['homes'] })
        queryClient.invalidateQueries({ queryKey: ['homeowners'] })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['work-orders'] })
        queryClient.invalidateQueries({ queryKey: ['regions'] })
        queryClient.invalidateQueries({ queryKey: ['priority'] })
        router.refresh()
      }, 100)
    })()
  }, [
    sessionLoading,
    loading,
    options,
    regionSlug,
    session?.role,
    pathname,
    searchParamsKey,
    router,
    queryClient,
    setProfile,
    profileSelectedRegionId,
  ])

  // Initialize selectedLocation from URL, explicit default, or profile preference
  useEffect(() => {
    if (loading || sessionLoading || options.length === 0) return

    if (regionSlug) {
      const regionFromUrl = options.find(opt => opt.slug === regionSlug)
      if (regionFromUrl) {
        setSelectedLocation(regionFromUrl.id)
        return
      }
    }

    if (defaultLocation) {
      const region = options.find(opt => opt.id === defaultLocation)
      if (region) {
        setSelectedLocation(region.id)
        return
      }
    }

    if (profileSelectedRegionId) {
      const region = options.find(opt => opt.id === profileSelectedRegionId)
      if (region) {
        setSelectedLocation(region.id)
        return
      }
    }

    if (!selectedLocation && options.length > 0) {
      setSelectedLocation(options[0].id)
    }
  }, [loading, sessionLoading, options, defaultLocation, profileSelectedRegionId, regionSlug, selectedLocation])

  const selectedOption = options.find(option => option.id === selectedLocation) || options[0]

  // Notify parent of initial selection
  useEffect(() => {
    if (selectedLocation && !loading && !sessionLoading) {
      onLocationChange?.(selectedLocation)
    }
  }, [selectedLocation, loading, sessionLoading, onLocationChange])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLocationSelect = async (locationId: string) => {
    const selectedRegion = options.find(opt => opt.id === locationId)
    if (!selectedRegion) return

    const previousSelection = selectedLocation

    // Optimistically update UI
    setSelectedLocation(locationId)
    setIsOpen(false)

    // Navigate to new region URL if we're on a region-aware route
    // Check if current pathname includes [region] pattern
    // Note: (dashboard) is a route group, so pathname is /[region]/... not /dashboard/[region]/...
    // We check if the first segment matches a known region slug or if we're already on a region route
    const currentRegionSlug = regionSlug
    const isRegionRoute = currentRegionSlug && pathname?.startsWith(`/${currentRegionSlug}`)

    if (isRegionRoute && selectedRegion.slug) {
      // Replace region in URL - pathname is /[region]/[rest-of-path]
      // Extract the path after the region segment
      const pathAfterRegion = pathname.replace(`/${currentRegionSlug}`, '') || '/'

      // Preserve query parameters
      const queryString = searchParams.toString()
      const newPath = `/${selectedRegion.slug}${pathAfterRegion}${queryString ? `?${queryString}` : ''}`

      // Use replace instead of push to avoid adding to history
      router.replace(newPath)

      // Invalidate all React Query caches after navigation
      // Use setTimeout to ensure navigation completes first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['homes'] })
        queryClient.invalidateQueries({ queryKey: ['homeowners'] })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['work-orders'] })
        queryClient.invalidateQueries({ queryKey: ['regions'] })
        queryClient.invalidateQueries({ queryKey: ['priority'] })
        // Force refresh to ensure server components re-render
        router.refresh()
      }, 100)
    } else if (selectedRegion.slug) {
      // If not on a region route yet, navigate to dashboard with region
      // This will be used when routes are restructured in Phase 4
      const newPath = buildRegionUrl(selectedRegion.slug, '/home')

      router.replace(newPath)

      // Invalidate all React Query caches after navigation
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['homes'] })
        queryClient.invalidateQueries({ queryKey: ['homeowners'] })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['work-orders'] })
        queryClient.invalidateQueries({ queryKey: ['regions'] })
        queryClient.invalidateQueries({ queryKey: ['priority'] })
        router.refresh()
      }, 100)
    }

    // Save to database in the background
    try {
      setSaving(true)
      const result = await updateSelectedRegion(locationId)

      if (result.success && result.data) {
        // Update profile context with fresh data (includes updated selectedRegion and fresh signed URL for profile picture)
        setProfile(result.data)
        // Cache is already invalidated by the server action
        // Force a fresh profile fetch to ensure we have the latest signed URL for the profile picture
        try {
          const freshProfile = await getProfile()
          if (freshProfile.success && freshProfile.data) {
            setProfile(freshProfile.data)
          }
        } catch (profileError) {
          console.warn('Failed to refresh profile after region update:', profileError)
          // Continue with the data from updateSelectedRegion even if refresh fails
        }
      } else if (!result.success) {
        console.error('Failed to update selected region:', result.message)
        // Revert on error - could add toast notification here
        if (previousSelection) {
          const previousRegion = options.find(opt => opt.id === previousSelection)
          if (previousRegion) {
            setSelectedLocation(previousSelection)
            onLocationChange?.(previousSelection)
          }
        }
      }
    } catch (error) {
      console.error('Error updating selected region:', error)
      // Revert on error
      if (previousSelection) {
        const previousRegion = options.find(opt => opt.id === previousSelection)
        if (previousRegion) {
          setSelectedLocation(previousSelection)
          onLocationChange?.(previousSelection)
        }
      }
    } finally {
      setSaving(false)
    }
  }

  // Format region slug for display when options aren't loaded yet (e.g. after region switch)
  const labelFromSlug = regionSlug
    ? regionSlug
        .split('-')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : null

  // Show loading only when we have no region context; otherwise show slug-derived label so region switch feels instant
  const showLoadingState = (loading || sessionLoading || !selectedOption) && !labelFromSlug

  if (showLoadingState) {
    return (
      <div className={cn("relative", className)}>
        <button
          disabled
          className="flex items-center gap-2 opacity-50"
          aria-expanded={false}
        >
          <h2 className="text-xl md:text-2xl font-semibold leading-none">
            Loading...
          </h2>
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>
    )
  }

  // While options are still loading but we have a region slug (e.g. just switched region), show slug-derived label
  const displayLabel = selectedOption?.label ?? labelFromSlug ?? 'Loading...'
  const isDisabled = loading || sessionLoading

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={saving || isDisabled}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-50"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <h2 className="text-xl md:text-2xl font-semibold leading-none">
          {displayLabel}
        </h2>
        <ChevronDown className={cn("w-5 h-5 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleLocationSelect(option.id)}
                className={cn(
                  "w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors",
                  selectedLocation === option.id && "bg-gray-100 font-medium"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LocationSelector
